/**
 * Feature E03-F02-T04 — Pre-Kickoff FRONT-END model (pure, DOM-free).
 *
 * The non-render rules of the Pre-Kickoff panel live here so the React component
 * (`PreKickoffPanel.tsx`) stays a thin shell and the unit tests
 * (`prekickoffModel.test.ts`) exercise the logic with no DOM. 1:1 port of the
 * Stage-1 (Pre-Kickoff) render in `../EER app/public/assets/js/project.js`
 * (~853-1052): the company/position brief, the three option cards
 * (A: Kick-off, B: Request Details, C: Hybrid) with the AI-Recommendation badge,
 * and the draft editor pre-fill (`openEmailDialog` ~1055).
 *
 * Source of truth for the shapes is `@eer/shared`
 * (`prekickoff-analysis.ts` — `PreKickoffAnalysis`, `PreKickoffDrafts`,
 * `PreKickoffDraftOption`).
 *
 * Two display invariants carried over from the legacy SPA:
 *   - the AI-Recommendation badge follows `analysis.recommendation` (the legacy
 *     UI hard-coded `recommended=true` on card C, but the model recommendation is
 *     the authoritative signal — v2 honours it: the badge marks whichever option
 *     the analysis recommends, defaulting to C);
 *   - a draft's recipient (`to`) is whatever the server persisted (always the
 *     project contact email, server-injected — never editable to a forged value
 *     here; the field is editable for typos but pre-filled from the draft).
 */
import {
  PRE_KICKOFF_DRAFT_OPTIONS,
  PRE_KICKOFF_DRAFT_TYPES,
  PRE_KICKOFF_ANALYSIS_TYPE,
  PRE_KICKOFF_ANALYSIS_EDITABLE_FIELDS,
  preKickoffAnalysisSchema,
  preKickoffDraftSchema,
  type PreKickoffAnalysis,
  type PreKickoffDraft,
  type PreKickoffDrafts,
  type PreKickoffDraftOption,
  type PreKickoffAnalysisEdit,
  type ProjectUpdates,
} from '@eer/shared';

/** The fixed labels + one-line descriptions for the three option cards. */
export const PRE_KICKOFF_OPTION_META: Record<
  PreKickoffDraftOption,
  { readonly title: string; readonly description: string }
> = {
  A: { title: 'Kick-off Meeting', description: 'Schedule a meeting right away.' },
  B: { title: 'Request Details', description: 'Request JD and brief in writing.' },
  C: {
    title: 'Hybrid (Meeting + Questions)',
    description: 'Schedule a meeting and send a list of questions.',
  },
} as const;

/** One row of the company/position brief (label + value), rendered read-only. */
export interface BriefField {
  readonly label: string;
  readonly value: string;
}

/** The two brief cards (Company + Position) assembled from the analysis. */
export interface PreKickoffBrief {
  /** 2-3 sentence inquiry summary (empty string when the model omitted it). */
  readonly summary: string;
  readonly company: readonly BriefField[];
  readonly position: readonly BriefField[];
  /** Bullet key-points / gaps the analysis surfaced. */
  readonly keyPoints: readonly string[];
}

/** Drop empty-value rows so the brief never shows blank `Label:` lines. */
function nonEmpty(fields: readonly BriefField[]): readonly BriefField[] {
  return fields.filter((f) => f.value.trim().length > 0);
}

/**
 * Build the Company + Position brief from the analysis (legacy `briefData`
 * ~828-839). `clientName` / `roleName` (the project header fields) take
 * precedence over the model's `company_name` / `position_title` when present,
 * mirroring the legacy fallback `p.client_name || analysisContent.company_name`.
 */
export function buildPreKickoffBrief(
  analysis: PreKickoffAnalysis,
  project?: { readonly clientName?: string | null; readonly roleName?: string | null },
): PreKickoffBrief {
  const companyName = (project?.clientName ?? '').trim() || analysis.company_name;
  const positionTitle = (project?.roleName ?? '').trim() || analysis.position_title;

  return {
    summary: analysis.summary,
    company: nonEmpty([
      { label: 'Name', value: companyName },
      { label: 'Industry', value: analysis.industry },
      { label: 'Type', value: analysis.company_type },
      { label: 'Offices', value: analysis.offices },
      { label: 'Products', value: analysis.products },
    ]),
    position: nonEmpty([
      { label: 'Title', value: positionTitle },
      { label: 'Country', value: analysis.country },
      { label: 'Scope', value: analysis.scope },
      { label: 'Requirements', value: analysis.key_requirements },
      { label: 'Role/Goal', value: analysis.role_goal },
    ]),
    keyPoints: analysis.key_points,
  };
}

/** A presented option card (label + copy + whether it carries the AI badge). */
export interface PreKickoffOptionCard {
  readonly option: PreKickoffDraftOption;
  readonly title: string;
  readonly description: string;
  /** True when `analysis.recommendation` selects this option (the AI badge). */
  readonly recommended: boolean;
  /** The persisted draft for this option (pre-fills the editor). */
  readonly draft: PreKickoffDraft;
}

/**
 * Build the three option cards from the analysis + the persisted drafts. The
 * card whose option equals `analysis.recommendation` carries the AI-Recommendation
 * badge (default `C` — the recommendation schema default). The cards are always
 * returned in A, B, C order (`PRE_KICKOFF_DRAFT_OPTIONS`).
 */
export function buildOptionCards(
  analysis: PreKickoffAnalysis,
  drafts: PreKickoffDrafts,
): readonly PreKickoffOptionCard[] {
  return PRE_KICKOFF_DRAFT_OPTIONS.map((option) => ({
    option,
    title: PRE_KICKOFF_OPTION_META[option].title,
    description: PRE_KICKOFF_OPTION_META[option].description,
    recommended: analysis.recommendation === option,
    draft: drafts[option],
  }));
}

/** Editable state of the draft email (to / subject / body). */
export interface DraftEditorState {
  readonly to: string;
  readonly subject: string;
  readonly body: string;
}

/**
 * Seed the draft editor from a persisted draft (legacy `openEmailDialog`
 * ~1075-1077: pre-fill to / subject / body). All three fields are then user-
 * editable; `to` keeps the server-injected contact email by default.
 */
export function draftToEditorState(draft: PreKickoffDraft): DraftEditorState {
  return {
    to: draft.to,
    subject: draft.subject,
    body: draft.body,
  };
}

/** True when the editor has a recipient + a non-blank body (send-readiness). */
export function isDraftSendable(state: DraftEditorState): boolean {
  return state.to.trim().length > 0 && state.body.trim().length > 0;
}

// ---------------------------------------------------------------------------
// Persisted-artifact hydration (read path)
// ---------------------------------------------------------------------------

/**
 * A persisted artifact row as it arrives inside the project detail card
 * (`@eer/shared` `DetailArtifact` — `type` + raw `content`). The panel reads
 * these on open so a previously-run analysis renders WITHOUT re-running the AI:
 * the four UPSERTed artifacts (T02 — `analysis` + `pre_kickoff_draft_<A|B|C>`)
 * are re-hydrated into the same `{ analysis, drafts }` shape the run mutation
 * returns. Anything else (kick-off questions, docs, …) is ignored.
 */
export interface PersistedArtifactRow {
  readonly type: string;
  readonly content?: unknown;
}

/** The hydrated read-side shape (mirrors the run response `{analysis,drafts}`). */
export interface PreKickoffArtifacts {
  readonly analysis: PreKickoffAnalysis;
  readonly drafts: PreKickoffDrafts;
}

/** Parse one artifact `content` through a schema, falling back to its defaults. */
function safeParse<T>(schema: { parse: (v: unknown) => T }, content: unknown, fallback: T): T {
  try {
    return schema.parse(content ?? {});
  } catch {
    return fallback;
  }
}

/**
 * Re-hydrate the persisted Pre-Kickoff artifacts from the project's artifact list
 * into the `{ analysis, drafts }` shape the panel renders. Returns `null` when the
 * project carries NO `analysis` artifact yet (the analysis was never run — the
 * panel shows the empty "Run AI Analysis" state). The three drafts default to the
 * schema defaults (empty draft) when a particular option artifact is absent, so a
 * partially-saved project still renders all three cards.
 *
 * Each `content` is parsed through the shared zod schema (the same one the route
 * validates), so a drifted/garbled stored shape degrades to defaults instead of
 * throwing — the read path never crashes the panel.
 */
export function parsePersistedArtifacts(
  artifacts: readonly PersistedArtifactRow[] | null | undefined,
): PreKickoffArtifacts | null {
  if (!artifacts || artifacts.length === 0) return null;

  const analysisRow = artifacts.find((a) => a.type === PRE_KICKOFF_ANALYSIS_TYPE);
  if (!analysisRow) return null;

  // Empty defaults for any draft option whose artifact is missing.
  const emptyDraft = preKickoffDraftSchema.parse({});
  const draftFor = (option: PreKickoffDraftOption): PreKickoffDraft => {
    const row = artifacts.find((a) => a.type === PRE_KICKOFF_DRAFT_TYPES[option]);
    return row ? safeParse(preKickoffDraftSchema, row.content, emptyDraft) : emptyDraft;
  };

  return {
    analysis: safeParse(
      preKickoffAnalysisSchema,
      analysisRow.content,
      preKickoffAnalysisSchema.parse({}),
    ),
    drafts: {
      A: draftFor('A'),
      B: draftFor('B'),
      C: draftFor('C'),
    },
  };
}

// ---------------------------------------------------------------------------
// "Pre-Kickoff Data" block — clickable AI Analysis + Context → detail dialog
// (legacy `renderPreKickoffPanel` ~1591-1611 + `openPreKickoffDetail` ~1664-1694).
// ---------------------------------------------------------------------------

/**
 * Truncate `s` to at most `n` chars, appending an ellipsis when cut (legacy
 * `truncate(s, n) => s.length > n ? s.substring(0, n) + '...' : s`, ~1600).
 */
export function truncate(s: string, n: number): string {
  return s.length > n ? `${s.substring(0, n)}…` : s;
}

/**
 * The single-line preview text for the clickable "AI Analysis" entry. Mirrors the
 * legacy fallback chain `summary || recommendation || text` (~1595) — the first
 * non-empty of summary / the recommendation label / the joined key points — so a
 * sparse analysis still shows something clickable. Empty when nothing is present.
 */
export function analysisPreviewText(analysis: PreKickoffAnalysis): string {
  const candidates = [
    analysis.summary.trim(),
    analysis.recommendation, // 'A' | 'B' | 'C' — always present (default 'C')
    analysis.key_points.join('; ').trim(),
  ];
  return candidates.find((c) => c.length > 0) ?? '';
}

/** One labelled section of the AI-Analysis detail dialog (legacy parts ~1679-1682). */
export interface AnalysisDetailSection {
  readonly label: string;
  /** A paragraph value, or a bullet list when `items` is set. */
  readonly value?: string;
  readonly items?: readonly string[];
}

/**
 * Build the AI-Analysis detail dialog body (legacy `openPreKickoffDetail('analysis')`
 * ~1673-1683): Summary, Recommendation and Key points sections, dropping any that
 * are empty. Returns `[]` when the analysis carries no data (the dialog then shows
 * the "No analysis data" placeholder — legacy fallback).
 */
export function buildAnalysisDetail(
  analysis: PreKickoffAnalysis,
): readonly AnalysisDetailSection[] {
  const sections: AnalysisDetailSection[] = [];
  if (analysis.summary.trim().length > 0) {
    sections.push({ label: 'Summary', value: analysis.summary });
  }
  if (analysis.recommendation) {
    sections.push({ label: 'Recommendation', value: `Option ${analysis.recommendation}` });
  }
  if (analysis.classification) {
    sections.push({ label: 'Classification', value: analysis.classification });
  }
  if (analysis.key_points.length > 0) {
    sections.push({ label: 'Key points', items: analysis.key_points });
  }
  return sections;
}

/**
 * The manual-context preview (legacy `manualContext = p.data?.manual_context?.text`
 * ~1596). Reads the project's `data.manual_context.text` defensively — the `data`
 * bag is `Record<string, unknown>`, so every hop is guarded; returns `''` when the
 * project carries no manual context (the Context entry is then hidden).
 */
export function readManualContext(data: Record<string, unknown> | null | undefined): string {
  if (!data || typeof data !== 'object') return '';
  const mc = (data as { manual_context?: unknown }).manual_context;
  if (!mc || typeof mc !== 'object') return '';
  const text = (mc as { text?: unknown }).text;
  return typeof text === 'string' ? text : '';
}

// ---------------------------------------------------------------------------
// "Last analyzed" attribution (E03-F02-T06) — near the Run/Re-run button
// ---------------------------------------------------------------------------

/**
 * Format the "last analyzed" attribution line shown next to the AI Analysis
 * button once an analysis has ever completed (E03-F02-T06 acceptance).
 * `at` is the backend `last_analysis_at` (or a fresh client-side run's own
 * timestamp — see `PreKickoffPanel`'s local override); `who` is whatever the
 * caller resolved as the actor's display value (a session user's name for a
 * just-completed run, or the persisted `last_analysis_by` id on reload —
 * mirrors the `authorLabel` convention in `projectTimelineModel.ts`: shown
 * as-is, no separate id→name lookup).
 *
 * Returns `null` when `at` is absent/unparsable — the panel then renders
 * nothing (never a run has ever completed, or the timestamp is malformed).
 */
export function formatLastAnalysis(
  at: string | null | undefined,
  who: string | null | undefined,
): string | null {
  if (!at) return null;
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return null;
  const when = `${d.toLocaleDateString()} ${d.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
  const whoTrimmed = (who ?? '').trim();
  return whoTrimmed.length > 0
    ? `last analyzed: ${when} · ${whoTrimmed}`
    : `last analyzed: ${when}`;
}

// ---------------------------------------------------------------------------
// PK4 (E03-F02-T08) — inline edit of the persisted analysis brief
//   PATCH /projects/:id/analysis (prekickoffApi.ts → useSaveAnalysis)
// ---------------------------------------------------------------------------

/**
 * Local edit-mode state for the PK4 brief editor — the 10 editable analysis
 * fields (`PRE_KICKOFF_ANALYSIS_EDITABLE_FIELDS`) as plain strings/array, seeded
 * from the loaded analysis and free-form thereafter (no validation on keystroke;
 * the route validates the patch on Save). Deliberately mirrors, field-for-field,
 * the editable subset `preKickoffAnalysisEditSchema` picks — NOT `company_name`
 * / `position_title` (identity fields edited via the project header) or
 * `classification` / `recommendation` (internal routing enums).
 */
export interface AnalysisEditState {
  readonly summary: string;
  readonly industry: string;
  readonly company_type: string;
  readonly offices: string;
  readonly products: string;
  readonly country: string;
  readonly scope: string;
  readonly key_requirements: string;
  readonly role_goal: string;
  readonly key_points: readonly string[];
}

/** Seed the PK4 edit state from the currently loaded analysis (Edit button). */
export function analysisToEditState(analysis: PreKickoffAnalysis): AnalysisEditState {
  return {
    summary: analysis.summary,
    industry: analysis.industry,
    company_type: analysis.company_type,
    offices: analysis.offices,
    products: analysis.products,
    country: analysis.country,
    scope: analysis.scope,
    key_requirements: analysis.key_requirements,
    role_goal: analysis.role_goal,
    key_points: analysis.key_points,
  };
}

/** The 9 free-text editable fields (everything in the editable set but `key_points`,
 * which needs array-vs-array comparison instead of `!==`). Derived from the shared
 * `PRE_KICKOFF_ANALYSIS_EDITABLE_FIELDS` so the two lists cannot drift apart. */
const EDITABLE_STRING_FIELDS = PRE_KICKOFF_ANALYSIS_EDITABLE_FIELDS.filter(
  (f) => f !== 'key_points',
) as readonly Exclude<(typeof PRE_KICKOFF_ANALYSIS_EDITABLE_FIELDS)[number], 'key_points'>[];

/** True when two `key_points` arrays are element-for-element equal. */
function keyPointsEqual(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

/**
 * Diff the PK4 edit state against the originally loaded analysis, returning ONLY
 * the fields that actually changed as a `PreKickoffAnalysisEdit` patch. The route
 * is a partial MERGE (an omitted key is left untouched server-side), so unchanged
 * fields must never ride along — sending them would be harmless but noisy, and
 * sending a *stale* value would clobber a concurrent edit made elsewhere between
 * load and save.
 *
 * Returns `null` when nothing changed. The caller MUST NOT call the route with an
 * empty patch: `preKickoffAnalysisEditSchema`'s `.refine` rejects it with a 400
 * ("nothing to save") — an empty Save should just close edit mode, not round-trip
 * to the server for a guaranteed 400.
 */
export function diffAnalysisEdit(
  original: PreKickoffAnalysis,
  edited: AnalysisEditState,
): PreKickoffAnalysisEdit | null {
  const patch: Record<string, string | string[]> = {};
  for (const key of EDITABLE_STRING_FIELDS) {
    if (edited[key] !== original[key]) patch[key] = edited[key];
  }
  if (!keyPointsEqual(edited.key_points, original.key_points)) {
    patch.key_points = [...edited.key_points];
  }
  return Object.keys(patch).length > 0 ? (patch as PreKickoffAnalysisEdit) : null;
}

// ---------------------------------------------------------------------------
// PK-QA-2 — "Apply to Project": copy company_name / position_title from the
// analysis into the project SCALARS (client_name / role_name) on an EXPLICIT
// click. Auto-sync stays forbidden (PK4 ADR); this is the one deliberate path.
//   PATCH /projects/:id (updateProject) — see decision-log 2026-07-20 PK-QA-2.
// ---------------------------------------------------------------------------

/** Which project scalar an apply-field targets (the PATCH /projects/:id key). */
export type ApplyFieldKey = 'client_name' | 'role_name';

/** Per-field outcome of comparing the AI suggestion to the current scalar. */
export type ApplyFieldAction =
  /** The analysis carries no suggestion for this field — nothing to apply. */
  | 'skip'
  /** The suggestion already equals the scalar — nothing to do (already applied). */
  | 'noop'
  /** The scalar is empty — apply the suggestion silently (no confirm). */
  | 'silent'
  /** The scalar is non-empty AND differs — needs an overwrite confirm. */
  | 'conflict';

/** One field's apply plan (label + old/new values + the decided action). */
export interface ApplyFieldPlan {
  readonly key: ApplyFieldKey;
  readonly label: string;
  /** The trimmed AI suggestion (the value that would be written). */
  readonly suggestion: string;
  /** The trimmed current project scalar. */
  readonly current: string;
  readonly action: ApplyFieldAction;
}

/** The whole "Apply to Project" plan for the two identity fields. */
export interface ApplyToProjectPlan {
  readonly fields: readonly ApplyFieldPlan[];
  /** Fields that will actually be written (action `silent` or `conflict`). */
  readonly toApply: readonly ApplyFieldPlan[];
  /** Fields whose non-empty scalar differs from the suggestion (need confirm). */
  readonly conflicts: readonly ApplyFieldPlan[];
  /** True when the Apply action can run (there is at least one field to write). */
  readonly canApply: boolean;
  /**
   * True when there is nothing to apply BECAUSE the project already equals every
   * non-empty suggestion — drives the subtle "Applied" marker. Distinct from the
   * "no suggestions at all" case (both empty), which shows neither button state.
   */
  readonly alreadyApplied: boolean;
}

/** Decide one field's action from its (trimmed) suggestion + current scalar. */
function applyFieldPlan(
  key: ApplyFieldKey,
  label: string,
  suggestionRaw: string,
  currentRaw: string,
): ApplyFieldPlan {
  const suggestion = suggestionRaw.trim();
  const current = currentRaw.trim();
  let action: ApplyFieldAction;
  if (suggestion.length === 0) action = 'skip';
  else if (current.length === 0) action = 'silent';
  else if (current === suggestion) action = 'noop';
  else action = 'conflict';
  return { key, label, suggestion, current, action };
}

/**
 * Build the "Apply to Project" plan (PK-QA-2): copy `analysis.company_name` →
 * `client_name` and `analysis.position_title` → `role_name`, deciding PER FIELD
 * independently whether the copy is silent (scalar empty or already equal) or
 * needs an overwrite confirm (scalar non-empty AND different). Contact is out of
 * scope — the analysis artifact carries no suggested contact.
 */
export function planApplyToProject(
  analysis: PreKickoffAnalysis,
  current: { readonly clientName: string; readonly roleName: string },
): ApplyToProjectPlan {
  const fields: readonly ApplyFieldPlan[] = [
    applyFieldPlan('client_name', 'Company Name', analysis.company_name, current.clientName),
    applyFieldPlan('role_name', 'Position Title', analysis.position_title, current.roleName),
  ];
  const toApply = fields.filter((f) => f.action === 'silent' || f.action === 'conflict');
  const conflicts = fields.filter((f) => f.action === 'conflict');
  const canApply = toApply.length > 0;
  const alreadyApplied = !canApply && fields.some((f) => f.action === 'noop');
  return { fields, toApply, conflicts, canApply, alreadyApplied };
}

/** Build the PATCH `updates` bag from the fields an apply plan will write. */
export function applyPlanToUpdates(plan: ApplyToProjectPlan): ProjectUpdates {
  const updates: ProjectUpdates = {};
  for (const f of plan.toApply) {
    if (f.key === 'client_name') updates.client_name = f.suggestion;
    else updates.role_name = f.suggestion;
  }
  return updates;
}
